import * as Sentry from '@sentry/react';
import { abbreviateNumber } from 'js-abbreviation-number';
import { NIL as NIL_UUID } from 'uuid';
import { eChannelType } from '../../providers/SocketsProvider/Sockets.enums';
import { tLead } from '../../providers/SocketsProvider/Sockets.types';
import { eUserRole } from '../../providers/UserProvider/User.enums';
import {
  tBoardExpiredLead,
  tBoardExpiredState,
  tBoardFilterParams,
  tBoardFilters,
  tFunnel,
  tFunnelLead,
  tFunnelLegacy,
  tFunnelStage,
  tFunnelStageLegacy,
  tFunnelStatus,
  tWorkspaceUserFromAPI,
  tFunnelStatusLegacy,
  tFunnelUser,
  tFunnelUserLegacy,
} from './Pipe.types';
import moment from 'moment';
import {
  INBOX_STAGE_NAME,
  ISO601Format,
  MINUTE_IN_MS,
  UNCLASSIFIED_UUID,
} from './Pipe.constants.ts';
import { tSlbEngagementState, tSlbPausedReason } from '../../services/AI/AI.types';
import { buildDateFilterParams } from '../../functions/date.functions';
import {
  carriedUndeliveredMark,
  resolveUndeliveredMark,
} from '../MessengerV2/Messenger.functions';

export function funnelParser(funnelLegacy: tFunnelLegacy): tFunnel {
  const stages = funnelLegacy.stages ?? [];
  const users = funnelLegacy.users ?? [];
  const statuses = funnelLegacy.statuses ?? [];
  const workspaceUsers = funnelLegacy.workspaceUsers ?? [];

  return {
    canViewHandle: funnelLegacy.can_view_handle,
    color: funnelLegacy.color,
    id: funnelLegacy.uuid,
    name: funnelLegacy.name,
    description: funnelLegacy.description,
    emoji: funnelLegacy.emoji,
    isDefault: funnelLegacy.is_default,
    isPublic: funnelLegacy.is_public,
    stages: [...stages]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((stage) => funnelStagesParser(stage)),
    users: users.map((user) => funnelUserParser(user)),
    statuses: statuses.map((status) => funnelStatusParser(status)),
    workspaceUsers: workspaceUsers.length && 'authID' in workspaceUsers[0] ?
      (workspaceUsers as tWorkspaceUserFromAPI[]) : undefined,
  };
}

export function funnelStagesParser(
  funnelStageLegacy: tFunnelStageLegacy,
): tFunnelStage {
  return {
    order: funnelStageLegacy.order,
    id: funnelStageLegacy.uuid,
    name: funnelStageLegacy.name,
    expiresAfter: funnelStageLegacy.expires_after,
    leadCount: funnelStageLegacy.count_leads ?? 0,
    sumValue: funnelStageLegacy.sum_value ?? '',
    leads: [],
    description: funnelStageLegacy.description,
    botEnabled: funnelStageLegacy.bot_enabled,
    isVirtual: funnelStageLegacy.is_virtual ?? false,
    // Absent stays absent so a merge can keep what the board holds.
    ...('opens_outcome' in funnelStageLegacy && { opens_outcome: funnelStageLegacy.opens_outcome }),
  };
}

/**
 * Merges a stage from a remote source (WebSocket event or HTTP response) over
 * the previous stage in client state. Preserves `sumValue`, `description`, and
 * `botEnabled` when the remote payload omits them — guards against backend
 * regressions (BUG-460 class: sparse payloads render as $0 in the UI).
 *
 * Also carries the client-held cursor state (`nextCursor`, `cursorPagination`)
 * forward: no remote stage payload ships it, and dropping it made the next
 * scroll look like the last page, freezing filtered columns at 25 (BUG-974).
 *
 * Does NOT preserve `leads` / `leadCount`: callers are responsible for those
 * since the correct behavior differs by source (WS events preserve prev, HTTP
 * stage-create uses [], HTTP stage-remove uses prev).
 *
 * Uses `||` (not `??`) for `sumValue` so empty string `""` also falls back —
 * legitimate zero values arrive as `"0.00"` (truthy) and pass through.
 *
 * Records a deduplicated Sentry breadcrumb when the fallback fires; see
 * `reportMissingSumValue` for the one-shot-per-stage-per-session semantics.
 */
export function mergeFunnelStageWithPrev(
  currentStage: tFunnelStage,
  prevStage: tFunnelStage | undefined,
): tFunnelStage {
  if (!prevStage) return currentStage;

  if (!currentStage.sumValue) {
    reportMissingSumValue(currentStage.id, currentStage.name);
  }

  return {
    ...currentStage,
    description: currentStage.description ?? prevStage.description,
    botEnabled: currentStage.botEnabled ?? prevStage.botEnabled,
    // LEA-50: an explicit null clears the mapping; only an absent key keeps it.
    opens_outcome: currentStage.opens_outcome !== undefined ? currentStage.opens_outcome : prevStage.opens_outcome,
    sumValue: currentStage.sumValue || prevStage.sumValue,
    ...pickStagePagination(currentStage, prevStage),
  };
}

/**
 * The cursor for a stage's next page lives only in client state (set by
 * `getMoreLeads`). Any code that rebuilds stages from a server payload must
 * carry it over, or the column stops paginating (BUG-974).
 */
export function pickStagePagination(
  currentStage: tFunnelStage,
  prevStage: tFunnelStage | undefined,
): Pick<tFunnelStage, 'nextCursor' | 'cursorPagination'> {
  return {
    nextCursor: currentStage.nextCursor ?? prevStage?.nextCursor,
    cursorPagination: currentStage.cursorPagination ?? prevStage?.cursorPagination,
  };
}

/**
 * Module-level dedup set so a backend regression affecting N stages only
 * produces N breadcrumbs per session — not N × every update. Without this,
 * the 100-slot Sentry breadcrumb ring buffer would be flooded within ~60s,
 * erasing the diagnostic trail for any unrelated error that fires after.
 */
const reportedMissingSumValue = new Set<string>();

function reportMissingSumValue(stageId: string, stageName: string): void {
  if (reportedMissingSumValue.has(stageId)) return;
  reportedMissingSumValue.add(stageId);
  Sentry.addBreadcrumb({
    category: 'pipe.stage_merge',
    level: 'warning',
    message: 'pipe.stage_merge.sum_value_missing',
    data: { stageId, stageName },
  });
}

/**
 * Test-only: clears the one-shot dedup set. Call in `beforeEach` when asserting
 * breadcrumb behavior; production code should never invoke this.
 */
export function _resetMissingSumValueReports(): void {
  reportedMissingSumValue.clear();
}

/**
 * Pure reducer for the `funnel_event.updated` WebSocket event. Extracted from
 * the hook so the stale-closure guard and merge semantics can be asserted
 * without mounting `usePipe`.
 *
 * Guard: returns `prevFunnel` unchanged when the event targets a different
 * funnel. This check MUST run against `prevFunnel.id` (not a render-captured
 * value) — see BUG-460 for the race this closes.
 *
 * Preserves from prev state:
 *   - virtual / inbox stages (not in the event payload)
 *   - client-maintained `leads` and `leadCount` per stage
 *   - `workspaceUsers`, `fromIndustryTemplate`
 *   - `users` when the event ships an empty list (treated as "no change")
 *
 * Delegates stage-field fallbacks (`sumValue`, `description`, `botEnabled`) to
 * `mergeFunnelStageWithPrev`.
 */
export function reduceFunnelUpdatedEvent(
  prevFunnel: tFunnel | null,
  eventFunnel: tFunnel,
): tFunnel | null {
  if (!prevFunnel) return null;
  if (eventFunnel.id !== prevFunnel.id) return prevFunnel;

  const prevStages = prevFunnel.stages;
  const prefixStages = prevStages.filter(
    (stage) => stage.isVirtual || stage.name === INBOX_STAGE_NAME,
  );
  const newStagesFromEvent = eventFunnel.stages
    .filter((stage) => !stage.isVirtual)
    .map((eventStage) => {
      const prevStage = prevStages.find((p) => p.id === eventStage.id);
      const merged = mergeFunnelStageWithPrev(eventStage, prevStage);
      return {
        ...merged,
        leads: prevStage?.leads ?? [],
        leadCount: prevStage?.leadCount ?? 0,
      };
    });

  return {
    ...eventFunnel,
    workspaceUsers: prevFunnel.workspaceUsers || eventFunnel.workspaceUsers,
    fromIndustryTemplate: prevFunnel.fromIndustryTemplate,
    users: eventFunnel.users.length ? eventFunnel.users : prevFunnel.users,
    stages: [...prefixStages, ...newStagesFromEvent],
  };
}

export type tApplyLeadCreatedOptions = {
  stage: tFunnelStage;
  newLead: tFunnelLead;
  eventFunnelUuid: string | undefined;
  eventStageUuid: string | undefined;
  workspaceHasInbox: boolean;
  currentFunnelId: string | undefined;
};

/**
 * Per-stage reducer for `lead.created` WebSocket events.
 *
 * Idempotent by `newLead.id`: if a lead with the same id already lives in the
 * stage (typically inserted by the optimistic POST-201 handler a beat earlier),
 * returns the stage unchanged so the WS echo never produces a second card or
 * double-increments `leadCount` (BUG-465). React's functional setter serializes
 * updates, so two events landing in rapid succession each see the prior result
 * via `prevFunnel` and the second is caught by the `alreadyExists` short-circuit.
 */
export function applyLeadCreatedToStage(
  options: tApplyLeadCreatedOptions,
): tFunnelStage {
  const {
    stage,
    newLead,
    eventFunnelUuid,
    eventStageUuid,
    workspaceHasInbox,
    currentFunnelId,
  } = options;

  const funnelStageLeadCount = stage.leadCount ?? 0;
  const leads = stage.leads ?? [];

  const leadShouldBeAddedToInbox =
    workspaceHasInbox &&
    stage.id === NIL_UUID &&
    !eventFunnelUuid &&
    !eventStageUuid;

  const leadShouldBeAddedToUnclassified =
    stage.id === UNCLASSIFIED_UUID &&
    !eventStageUuid &&
    eventFunnelUuid === currentFunnelId;

  const isTargetStage =
    leadShouldBeAddedToInbox ||
    leadShouldBeAddedToUnclassified ||
    stage.id === eventStageUuid;

  if (!isTargetStage) return stage;

  const alreadyExists = leads.some((lead) => lead.id === newLead.id);
  if (alreadyExists) return stage;

  return {
    ...stage,
    leads: [newLead, ...leads],
    leadCount: funnelStageLeadCount + 1,
  };
}

/**
 * Parse a `sum_value` string (raw-decimal like "1600.00" or comma-separated like
 * "1,600.00") into a finite number. Returns 0 for undefined/empty/NaN inputs.
 * Extracted for testability and to remove the implicit `|| 0` collapse that
 * previously made `0` and `NaN` indistinguishable.
 */
export function parseStageSumValue(raw: string | undefined): number {
  if (raw === undefined || raw === '') return 0;
  const n = Number(raw.replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Sorts leads so that SLB-escalated (handoff pending) leads appear first,
 * preserving `last_action_at` order within each group via a stable sort.
 * Pure function — callers own the SLB feature-flag / integration guards
 * and decide whether to call it at all.
 *
 * Never mutates `leads`; returns a new array.
 */
export function sortLeadsWithEscalatedFirst(
  leads: tFunnelLead[],
  escalatedIds: Set<string>,
): tFunnelLead[] {
  return [...leads].sort((a, b) => {
    const aEscalated = escalatedIds.has(a.id) ? 1 : 0;
    const bEscalated = escalatedIds.has(b.id) ? 1 : 0;
    return bEscalated - aEscalated;
  });
}

export function funnelStageLegacyParser(
  funnelStage: tFunnelStage,
): tFunnelStageLegacy {
  return {
    order: funnelStage.order,
    uuid: funnelStage.id,
    name: funnelStage.name,
    expires_after: funnelStage.expiresAfter,
    count_leads: funnelStage.leadCount,
    sum_value: funnelStage.sumValue,
    description: funnelStage.description,
    bot_enabled: funnelStage.botEnabled,
  };
}

export function funnelLeadParser(
  leadEvent: tLead,
  defaultChannel: eChannelType,
  existingLead?: tFunnelLead,
): tFunnelLead {
  let lastMessageOn = '';

  if (leadEvent.message) {
    const localTimeDate = convertStringifiedDateToLocalTime(
      leadEvent.message.last_message_on,
    );

    lastMessageOn = formatDate(localTimeDate, ISO601Format);
  }

  return {
    id: leadEvent.uuid,
    handle: leadEvent.handle,
    expired: leadEvent.expired,
    // KTD5 — normalised through the same converter `last_message_on` uses, so
    // the socket's zone-less UTC string and the BFF board's RFC3339 string
    // reach `isExpired` as the same instant. Absent means "this event has no
    // opinion" (an older producer, a partial update), so the stamp already on
    // the card wins — the same rule `nextAppointmentAt` follows below.
    lastActionAt: leadEvent.last_action_at
      ? formatDate(
          convertStringifiedDateToLocalTime(leadEvent.last_action_at),
          ISO601Format,
        )
      : existingLead?.lastActionAt,
    name: leadEvent.full_name,
    profilePicture: leadEvent.profile_picture,
    value: `${leadEvent.value}`,
    hasCalendarEvent: false,
    assignedUser: leadEvent.user
      ? {
          authID: leadEvent.user?.auth_id,
          name: leadEvent.user?.name,
        }
      : null,
    status: {
      id: leadEvent.status?.uuid,
      name: leadEvent.status?.name,
    },
    chat: leadEvent.message
      ? {
          read: leadEvent.message.read,
          unreadCount: leadEvent.message.unread_messages,
          lastMessageOn: lastMessageOn,
          integration:
            eChannelType[leadEvent.message.channel_type] || defaultChannel,
          // Null is a known textless message; omission leaves the preview unknown.
          lastMessageText:
            leadEvent.message.last_message === null
              ? ''
              : leadEvent.message.last_message ?? existingLead?.chat?.lastMessageText,
          lastMessageFromMe: leadEvent.message.last_message_from_me ?? existingLead?.chat?.lastMessageFromMe,
          firstMessage: leadEvent.message.first_message ?? existingLead?.chat?.firstMessage,
          firstMessageOn: leadEvent.message.first_message_on ?? existingLead?.chat?.firstMessageOn,
          aiRespondEnabled: leadEvent.message.ai_respond_enabled ?? existingLead?.chat?.aiRespondEnabled,
        }
      : existingLead?.chat ?? null,
    leadbotState: leadEvent.leadbot_state,
    // Absent and null mean different things here. An event that omits the
    // field carries no opinion about the appointment (older producers, or
    // partial updates), so the value already on the card wins. An explicit
    // null is the backend saying the appointment is gone, and must clear it.
    nextAppointmentAt:
      leadEvent.next_appointment_at === undefined
        ? existingLead?.nextAppointmentAt
        : leadEvent.next_appointment_at ?? undefined,
    // INT-1597: a frame that omits the mark keeps the card's, or every
    // `lead.updated` (a stage move, a new message) would wipe "No entregó".
    ...resolveUndeliveredMark(leadEvent, existingLead),
  };
}

/**
 * INT-1596: whether any board filter is on. A filtered column reads Leads
 * Service's list in cursor mode; an unfiltered one reads the in-stage route,
 * which applies none of them. Forgetting a dimension here fails silently: the
 * column would load through in-stage and show the unfiltered population.
 */
export function hasActiveBoardFilters(filters: tBoardFilters): boolean {
  const [startDate, endDate] = filters.dateRange ?? [undefined, undefined];
  return Boolean(
    filters.messageReadFilter ||
      startDate ||
      endDate ||
      filters.integrationFilter ||
      filters.statusFilter ||
      filters.tagsFilter ||
      filters.assignedUserFilters ||
      filters.undeliveredFilter,
  );
}

/**
 * INT-1596: the filters a board column sends to /leads/filter. The
 * "No entregados (N)" count sends the same object, so the chip can never count
 * under a different mapping than the columns list.
 */
export function buildBoardFilterParams(filters: tBoardFilters): tBoardFilterParams {
  return {
    ...(filters.messageReadFilter && {
      read: filters.messageReadFilter === 'read',
    }),
    ...buildDateFilterParams(
      filters.dateRange ?? [undefined, undefined],
      filters.dateFilterType,
    ),
    ...(filters.integrationFilter && { integration: filters.integrationFilter }),
    ...(filters.statusFilter && { statusName: filters.statusFilter.name }),
    ...(filters.tagsFilter && {
      tags: `[${filters.tagsFilter
        .map((tag) => `${tag.id}:"${tag.name}"`)
        .join(',')}]`,
    }),
    ...(filters.assignedUserFilters && {
      assignedUserId: filters.assignedUserFilters.authId,
    }),
  };
}

/**
 * INT-1597: whether a socket frame's lead carries the undelivered mark. A
 * frame that states the mark decides; one that does not keeps the card's, the
 * same rule `funnelLeadParser` applies.
 */
export function isLeadUndelivered(
  leadEvent: tLead,
  existingLead?: Pick<tFunnelLead, 'lastMessageFailed'>,
): boolean {
  return (
    carriedUndeliveredMark(leadEvent)?.lastMessageFailed ??
    existingLead?.lastMessageFailed === true
  );
}

export function funnelUserParser(
  funnelUserLegacy: tFunnelUserLegacy,
): tFunnelUser {
  return {
    autoAssign: funnelUserLegacy.autoassignable,
    name: funnelUserLegacy.name,
    profilePicture: funnelUserLegacy.profile_picture || null,
    role: funnelUserLegacy.role,
    authID: funnelUserLegacy.auth_id,
    id: funnelUserLegacy.uuid,
  };
}

export function funnelUserLegacyParser(
  funnelUser: tFunnelUser,
): tFunnelUserLegacy {
  return {
    autoassignable: funnelUser.autoAssign,
    name: funnelUser.name,
    profile_picture: funnelUser.profilePicture || undefined,
    role: funnelUser.role,
    auth_id: funnelUser.authID,
    uuid: funnelUser.id,
  };
}

export function funnelStatusParser(
  funnelStatusLegacy: tFunnelStatusLegacy,
): tFunnelStatus {
  return {
    id: funnelStatusLegacy.uuid,
    name: funnelStatusLegacy.name,
  };
}

export function abbreviateNumberForContainer(value: number) {
  let abbreviatedValue = '0';

  if (value) {
    abbreviatedValue = abbreviateNumber(value, 3, {
      symbols: ['', 'k', 'M', 'MM', 'B', 'MB', 'T'],
    });
    // Strip trailing zeros after decimal: "1.500k" → "1.5k", "1.000k" → "1k"
    abbreviatedValue = abbreviatedValue
      .replace(/(\.\d*[1-9])0+(?=[kMBT]|$)/, '$1') // remove zeros after last non-zero decimal
      .replace(/\.0+(?=[kMBT]|$)/, ''); // remove decimal point when only zeros follow
  }

  return `$${abbreviatedValue}`;
}

/**
 * Returns the passed date as a string using the passed format.
 * If the passed date is invalid, an empty string is returned.
 * @param {Date} date - The date to format
 * @param {string} [format=DD/M/YYYY, HH:mm:ss] - The format to apply
 */
export function formatDate(date: Date, format: string): string {
  const momentDate = moment(date);

  if (!momentDate.isValid()) {
    return '';
  }

  return momentDate.format(format);
}

/**
 * Converts the passed stringified date to the user's local time.
 * If the passed date is invalid, an invalid date is returned.
 * @param {string} stringifiedDate - The stringified date to convert
 * @returns {Date} The converted date
 */
export function convertStringifiedDateToLocalTime(
  stringifiedDate: string,
): Date {
  const momentDate = moment(stringifiedDate);
  const offset = new Date().getTimezoneOffset() / 60;
  const dateUTCMoment = moment(momentDate).subtract(offset, 'h');

  return dateUTCMoment.toDate();
}

/**
 * RET-1182 cold-load seeding: decide whether to seed SmartLeadBot engagement
 * state for the current funnel.
 *
 * Order-independent — replaces the earlier flag-flip detection that broke on cold
 * load. The flip approach asked "did `isSmartLeadBotEnabled` change false→true at
 * the right moment?", which silently failed when the feature flag resolved BEFORE
 * the funnel finished loading: the flip was consumed while `currentFunnel` was
 * still null, then never re-fired, so chips stayed empty until a manual reload.
 *
 * This models the actual intent instead: seed exactly once per funnel as soon as
 * ALL preconditions hold — agent enabled, a funnel WITH leads is loaded, and this
 * funnel hasn't been seeded yet — regardless of which async resolved first. A new
 * funnel id re-seeds; the same id never double-seeds.
 * @returns {boolean} true iff the SLB bulk state should be seeded now
 */
export function shouldSeedSlbState(input: {
  alreadySeededFunnelId: string | null;
  isSmartLeadBotEnabled: boolean;
  currentFunnelId: string | undefined;
  leadCount: number;
}): boolean {
  const {
    alreadySeededFunnelId,
    isSmartLeadBotEnabled,
    currentFunnelId,
    leadCount,
  } = input;
  if (!isSmartLeadBotEnabled) return false;
  if (!currentFunnelId) return false;
  if (leadCount === 0) return false;
  return alreadySeededFunnelId !== currentFunnelId;
}

/**
 * Whether the per-stage lead counts of the funnel on screen can be read as a
 * settled snapshot.
 *
 * The funnel-info response carries the board structure with `leadCount` still at
 * its zero default; each stage's real count only lands once its lead page
 * resolves. Consumers that capture the counts at a single moment (rather than
 * rendering them as they arrive) need to know when that batch is done, or they
 * capture an empty board for a board that has leads.
 *
 * The marker is compared against the funnel on screen instead of being a plain
 * boolean, so a funnel switch invalidates it for free: the previous funnel's
 * settled batch says nothing about the new one. A funnel with no stages has
 * nothing to load and is settled immediately.
 *
 * @param input Settle marker, the funnel on screen, and its stage count.
 * @returns {boolean} true iff the current funnel's stage counts are settled
 */
export function areStageLeadCountsLoaded(input: {
  leadCountsLoadedFunnelId: string | null;
  currentFunnelId: string | undefined;
  stageCount: number;
}): boolean {
  const { leadCountsLoadedFunnelId, currentFunnelId, stageCount } = input;
  if (!currentFunnelId) return false;
  if (stageCount === 0) return true;
  return leadCountsLoadedFunnelId === currentFunnelId;
}

/**
 * Normalize a funnel description for submission. Trims surrounding whitespace so
 * a whitespace-only value (e.g. a stray newline) is sent as an empty string
 * rather than tripping backend validation (BUG-546). Returns '' for null/undefined.
 * @param {string | null | undefined} description - the raw description input
 * @returns {string} the trimmed description (empty string when blank)
 */
export function normalizeFunnelDescription(
  description: string | null | undefined,
): string {
  return (description ?? '').trim();
}

export type tSlbEngagement = {
  engagementState: tSlbEngagementState;
  pausedReason?: tSlbPausedReason;
};

/**
 * Merge SmartLeadBot engagement state from a bulk auto-respond response into the
 * existing per-lead engagement map.
 *
 * Shared by BOTH bulk-state paths in usePipe — the combined full-funnel
 * `fetchSlbBulkState` AND the per-stage / pagination loader — so they can never
 * diverge again. The original RET-1182 prod regression was exactly that
 * divergence: the pagination path silently omitted the engagement merge, so
 * leads paged in after the first screen had no engagement state and every
 * Transferido (PAUSED_*) chip fell back to the legacy stage-gated path and never
 * rendered outside a bot stage.
 *
 * Leads whose `engagementState` is empty (pre-backfill sessions) are skipped;
 * existing entries are preserved.
 */
export function mergeEngagementByLead(
  prev: Map<string, tSlbEngagement>,
  states: ReadonlyArray<{
    leadId: string;
    engagementState?: tSlbEngagementState;
    pausedReason?: tSlbPausedReason;
  }>,
): Map<string, tSlbEngagement> {
  const next = new Map(prev);
  for (const state of states) {
    if (state.engagementState) {
      next.set(state.leadId, {
        engagementState: state.engagementState,
        pausedReason: state.pausedReason,
      });
    }
  }
  return next;
}

export function applyFilterMismatchRemoval(params: {
  stages: tFunnelStage[];
  leadId: string;
  stageToDecrement: string | undefined;
  wasLeadVisible: boolean;
}): tFunnelStage[] {
  const { stages, leadId, stageToDecrement, wasLeadVisible } = params;
  return stages.map((stage) => {
    const leadExistsInStage = stage.leads?.some((lead) => lead.id === leadId);
    if (!leadExistsInStage) return stage;

    const { leadCount = 0 } = stage;
    return {
      ...stage,
      leads: stage.leads?.filter((lead) => lead.id !== leadId) || [],
      leadCount:
        stage.id === stageToDecrement && wasLeadVisible
          ? Math.max(0, leadCount - 1)
          : leadCount,
    };
  });
}

/**
 * Has this lead left the seller's board?
 *
 * Mirrors the server's own visibility scope, `HasForSeller::scopeForSellerPerFunnel`
 * (leads-service) — on a private funnel a SELLER sees `user_uuid = me OR user_uuid IS NULL`:
 *
 *   $ownershipScope->where('leads.user_uuid', $userUuid)->orWhereNull('leads.user_uuid');
 *
 * So an **unassigned** lead belongs to the shared pool every funnel member can see, and
 * removing it here contradicts the list the very next refresh returns.
 *
 * BUG-884 — `assignedUserId != null` is what makes that true. Before it, an unassigned
 * lead (`user: null` on the socket payload → `undefined`) satisfied `!== currentUserId`,
 * so the seller's board dropped a lead it can legitimately see, and `Pipe.hook.tsx`
 * force-closed its chat modal mid-compose on every single inbound or outbound message.
 * The bug was unobservable until BUG-848 (leads-service #1415, prod 2026-09-03) widened
 * the `lead_event.updated` audience to deliver these events to sellers at all.
 *
 * Reassignment (BUG-579) is unaffected: a lead handed to another seller still has an
 * assignee, so it is still removed.
 */
export function shouldRemoveLeadFromFunnel(params: {
  currentUserRole: eUserRole;
  currentUserId: string | undefined;
  assignedUserId: string | null | undefined;
  funnelIsPublic: boolean | null | undefined;
  leadHasFunnel: boolean;
}): boolean {
  const { currentUserRole, currentUserId, assignedUserId, funnelIsPublic, leadHasFunnel } = params;
  return (
    currentUserId != null &&
    currentUserRole === eUserRole.SELLER &&
    !funnelIsPublic &&
    assignedUserId != null &&
    assignedUserId !== currentUserId &&
    leadHasFunnel
  );
}

/**
 * Tri-state for the column's "select all" control.
 * `some` is the indeterminate state.
 */
export type tSelectionHeaderState = 'none' | 'some' | 'all';

/**
 * Derive the select-all control's state from the leads currently rendered in the
 * column and the ids the user has selected.
 *
 * Scoped to the loaded page on purpose: the column paginates, so the selection
 * legitimately holds ids that are not on screen. Those must not make the visible
 * page read as partially selected, and an empty column must read `none` rather
 * than vacuously `all`.
 *
 * @param {object} input - loaded lead ids and the current selection
 * @returns {tSelectionHeaderState} the control's tri-state
 */
export function deriveSelectionHeaderState(input: {
  loadedLeadIds: string[];
  selectedIds: Set<string>;
}): tSelectionHeaderState {
  const { loadedLeadIds, selectedIds } = input;

  if (loadedLeadIds.length === 0) return 'none';

  let selectedOnPage = 0;
  for (const id of loadedLeadIds) {
    if (selectedIds.has(id)) selectedOnPage++;
  }

  if (selectedOnPage === 0) return 'none';
  return selectedOnPage === loadedLeadIds.length ? 'all' : 'some';
}

/**
 * Reconcile a selection snapshot against the stage's current leads.
 *
 * The board is live for the whole team, so a lead can move stage or be archived
 * by someone else between the user confirming and the request being sent. Those
 * ids are reported as dropped rather than submitted, so the result can say what
 * fell out instead of silently acting on a stale set.
 *
 * Snapshot order is preserved so the caller can name the dropped leads in the
 * order the user saw them.
 *
 * @param {object} input - the confirmed snapshot and the stage's current leads
 * @returns {{present: string[], dropped: string[]}} ids still in the stage, and ids that left
 */
export function reconcileSelectionSnapshot(input: {
  snapshotIds: string[];
  currentLeadIds: string[];
}): { present: string[]; dropped: string[] } {
  const { snapshotIds, currentLeadIds } = input;
  const current = new Set(currentLeadIds);

  const present: string[] = [];
  const dropped: string[] = [];

  for (const id of snapshotIds) {
    if (current.has(id)) present.push(id);
    else dropped.push(id);
  }

  return { present, dropped };
}

/**
 * Whether the column may offer "select all".
 *
 * Gated on the stage-count batch having settled, because the board is on screen
 * and interactive while every `leadCount` is still at its zero default — an
 * offer made in that window advertises zero leads. Composes
 * `areStageLeadCountsLoaded` rather than re-deriving readiness, and never gates
 * on a negated in-flight flag: `loadingFilteredLeads` reads false during the
 * debounce that precedes the fetch.
 *
 * @param {object} input - count-readiness inputs plus the column's loaded lead count
 * @returns {boolean} true when select-all can be offered
 */
export function canOfferSelectAll(input: {
  leadCountsLoadedFunnelId: string | null;
  currentFunnelId: string | undefined;
  stageCount: number;
  loadedLeadCount: number;
}): boolean {
  const { leadCountsLoadedFunnelId, currentFunnelId, stageCount, loadedLeadCount } = input;

  if (loadedLeadCount <= 0) return false;

  return areStageLeadCountsLoaded({
    leadCountsLoadedFunnelId,
    currentFunnelId,
    stageCount,
  });
}

/**
 * Fold a completed bulk action back into the board.
 *
 * The bulk endpoint returns no lead payloads, and nothing refetches the funnel
 * afterwards, so without this the mutation succeeds server-side and the column
 * keeps rendering the pre-action state until a manual reload. Mirrors how
 * `removeStage` already reconciles its own stage-wide bulk-edit: local,
 * post-success, no refetch.
 *
 * Applied only after the request resolves, so this is a reconciliation rather
 * than an optimistic guess — a failed action leaves the board untouched.
 *
 * @param {object} input - the stages plus what the action did
 * @returns {tFunnelStage[]} the stages with the action reflected
 */
export function applyBulkResultToStages(input: {
  stages: tFunnelStage[];
  action: 'reassign' | 'move';
  leadIds: string[];
  sourceStageId: string;
  assignedUser?: { authID: string; name: string } | null;
  destinationStageId?: string;
}): tFunnelStage[] {
  const {
    stages,
    action,
    leadIds,
    sourceStageId,
    assignedUser,
    destinationStageId,
  } = input;

  const moved = new Set(leadIds);
  if (moved.size === 0) return stages;

  if (action === 'reassign') {
    return stages.map((stage) => {
      if (stage.id !== sourceStageId) return stage;
      return {
        ...stage,
        leads: (stage.leads ?? []).map((lead) =>
          moved.has(lead.id) ? { ...lead, assignedUser: assignedUser ?? null } : lead,
        ),
      };
    });
  }

  if (!destinationStageId) return stages;

  // Captured before the map so the destination receives the same objects the
  // source is dropping, rather than re-deriving them from a mutated list.
  const source = stages.find((stage) => stage.id === sourceStageId);
  const travelling = (source?.leads ?? []).filter((lead) => moved.has(lead.id));
  if (travelling.length === 0) return stages;

  return stages.map((stage) => {
    if (stage.id === sourceStageId) {
      return {
        ...stage,
        leads: (stage.leads ?? []).filter((lead) => !moved.has(lead.id)),
        // Never below zero: leadCount is the server-side total and the selection
        // can only ever be a subset of it, but a stale count must not go negative.
        leadCount: Math.max(0, (stage.leadCount ?? 0) - travelling.length),
      };
    }
    if (stage.id === destinationStageId) {
      return {
        ...stage,
        leads: [...travelling, ...(stage.leads ?? [])],
        leadCount: (stage.leadCount ?? 0) + travelling.length,
      };
    }
    return stage;
  });
}

/**
 * The denominator for "N selected of M" in the selection action bar.
 *
 * Returns null while the per-stage count batch is still in flight. `leadCount`
 * sits at its zero default until then, and "25 seleccionados de 0" reads as a
 * defect rather than as a loading state — the bar falls back to the bare count
 * instead. Composes `areStageLeadCountsLoaded` for the same reason
 * `canOfferSelectAll` does, rather than re-deriving readiness.
 *
 * The stage count is filter-aware, so with filters on this is the size of the
 * filtered column — which is the set the user is actually looking at, and the
 * one the selection can reach.
 *
 * @param {object} input - count-readiness inputs plus the column's server-side total
 * @returns {number | null} the total, or null while it cannot be trusted
 */
export function resolveSelectionTotal(input: {
  leadCountsLoadedFunnelId: string | null;
  currentFunnelId: string | undefined;
  stageCount: number;
  stageLeadCount: number;
}): number | null {
  const {
    leadCountsLoadedFunnelId,
    currentFunnelId,
    stageCount,
    stageLeadCount,
  } = input;

  const loaded = areStageLeadCountsLoaded({
    leadCountsLoadedFunnelId,
    currentFunnelId,
    stageCount,
  });

  return loaded ? stageLeadCount : null;
}

/**
 * Whether a selection may be submitted to a bulk mutation.
 *
 * BUG-724: an empty selection reaching `bulk-edit` was interpreted downstream as
 * "every lead in the workspace" and mass-reassigned 42,606 leads. The empty case
 * is refused here so no request is built at all, rather than relying on the
 * backend's own guard.
 *
 * The cap is supplied by the caller so it can be sized against the binding
 * downstream timeout rather than hard-coded here.
 *
 * @param {object} input - the selected count and the caller's cap
 * @returns {boolean} true when the selection is non-empty and within the cap
 */
export function isSelectionSubmittable(input: {
  selectedCount: number;
  cap: number;
}): boolean {
  const { selectedCount, cap } = input;
  return selectedCount > 0 && selectedCount <= cap;
}

/**
 * Column-scoped lead selection. `stageId` is null when selection mode is closed.
 */
export type tSelectionState = {
  stageId: string | null;
  leadIds: Set<string>;
  /**
   * The last lead toggled on its own, and the fixed end of a shift-click range.
   * Held here rather than in the card so entering and leaving selection mode
   * clears it without a separate reset.
   */
  anchorId: string | null;
};

/** The closed, empty selection. */
export const EMPTY_SELECTION: tSelectionState = {
  stageId: null,
  leadIds: new Set<string>(),
  anchorId: null,
};

/** What a completed bulk selection action did, for folding back into the board. */
export type tBulkSelectionResult = {
  action: 'reassign' | 'move';
  leadIds: string[];
  sourceStageId: string;
  assignedUser?: { authID: string; name: string } | null;
  destinationStageId?: string;
};

export type tSelectionAction =
  | { type: 'enter'; stageId: string }
  | { type: 'exit' }
  | { type: 'toggle'; leadId: string }
  | { type: 'range'; leadId: string; orderedLeadIds: string[] }
  | { type: 'selectAllLoaded'; loadedLeadIds: string[] };

/**
 * Pure transition for the column selection.
 *
 * Kept out of the Pipe hook so the rules are unit-testable: the hook holds the
 * state and dispatches, this owns what each action means.
 *
 * Two invariants worth stating, because breaking either is silent:
 * - Every mutation returns a NEW Set. React and `useLocalStorageState` both bail
 *   on `Object.is`, so mutating in place and returning the same reference would
 *   produce no re-render and no write.
 * - `enter` replaces rather than merges. The selection belongs to one column, so
 *   opening it elsewhere must not carry the previous column's leads across.
 *
 * @param {tSelectionState} state - the current selection
 * @param {tSelectionAction} action - the transition to apply
 * @returns {tSelectionState} the next selection
 */
export function reduceSelection(
  state: tSelectionState,
  action: tSelectionAction,
): tSelectionState {
  switch (action.type) {
    case 'enter':
      return {
        stageId: action.stageId,
        leadIds: new Set<string>(),
        anchorId: null,
      };

    case 'exit':
      return EMPTY_SELECTION;

    case 'toggle': {
      if (state.stageId === null) return state;

      const leadIds = new Set(state.leadIds);
      if (leadIds.has(action.leadId)) leadIds.delete(action.leadId);
      else leadIds.add(action.leadId);

      // Every plain click re-anchors, so the next shift-click measures from
      // where the user last was rather than from the start of the column.
      return { stageId: state.stageId, leadIds, anchorId: action.leadId };
    }

    // Shift-click. The range is *added*, never replaced: unlike a file manager,
    // this selection is also fed by individual clicks and by select-all, and
    // re-issuing a range that deselected its previous span would silently drop
    // leads the user had picked deliberately. Overshooting is corrected by
    // clicking the extra cards, or by leaving selection mode.
    case 'range': {
      if (state.stageId === null) return state;

      const { leadId, orderedLeadIds } = action;
      const to = orderedLeadIds.indexOf(leadId);
      const from =
        state.anchorId === null ? -1 : orderedLeadIds.indexOf(state.anchorId);

      // No anchor yet, or either end scrolled out of the loaded page: fall back
      // to a plain toggle so the click is never inert.
      if (from === -1 || to === -1) {
        return reduceSelection(state, { type: 'toggle', leadId });
      }

      const leadIds = new Set(state.leadIds);
      for (let i = Math.min(from, to); i <= Math.max(from, to); i += 1) {
        leadIds.add(orderedLeadIds[i]);
      }

      // The anchor stays put, so a second shift-click re-measures from the same
      // origin instead of walking down the column.
      return { stageId: state.stageId, leadIds, anchorId: state.anchorId };
    }

    case 'selectAllLoaded': {
      if (state.stageId === null) return state;

      const { loadedLeadIds } = action;
      const leadIds = new Set(state.leadIds);
      const allLoadedSelected =
        loadedLeadIds.length > 0 &&
        loadedLeadIds.every((id) => leadIds.has(id));

      // Deselecting the visible page leaves ids the user picked on other pages
      // alone — that is a narrower action than clearing the whole selection.
      if (allLoadedSelected) loadedLeadIds.forEach((id) => leadIds.delete(id));
      else loadedLeadIds.forEach((id) => leadIds.add(id));

      return { stageId: state.stageId, leadIds, anchorId: state.anchorId };
    }

    default:
      return state;
  }
}

export type tBulkEditPayload = {
  filters: { leads: string[] };
  set: Record<string, string | boolean | null>;
};

/**
 * Build the bulk-edit request for a confirmed selection.
 *
 * `filters.leads` is always an explicit, non-empty id list. BUG-724: a
 * bulk-edit whose filters resolved to nothing was read downstream as "every
 * lead in the workspace" and mass-reassigned 42,606 leads, so this throws
 * rather than emitting a request that could be interpreted that way.
 *
 * The `set` shape mirrors what the funnel already sends when a stage is deleted
 * and its leads migrate.
 *
 * @param {object} input - the action, its target, and the reconciled lead ids
 * @returns {tBulkEditPayload} the request body
 */
export function buildBulkEditPayload(input: {
  action: 'reassign' | 'move';
  leadIds: string[];
  funnelId: string;
  userAuthId?: string;
  stageId?: string;
}): tBulkEditPayload {
  const { action, leadIds, funnelId, userAuthId, stageId } = input;

  if (leadIds.length === 0) {
    throw new Error('Refusing to build a bulk-edit with no leads');
  }

  if (action === 'reassign') {
    if (!userAuthId) throw new Error('Reassign requires a target advisor');
    return { filters: { leads: leadIds }, set: { user_uuid: userAuthId } };
  }

  if (!stageId) throw new Error('Move requires a destination stage');
  return {
    filters: { leads: leadIds },
    set: { funnel_uuid: funnelId, stage_uuid: stageId, in_inbox: false },
  };
}

/**
 * Map a failed bulk action to a translation key.
 *
 * A generic toast is not acceptable here: the backend returns a specific reason
 * and discarding it once had users retrying a doomed save a dozen times. The
 * server's own message is preferred when present; the generic key is the last
 * resort, not the default.
 *
 * @param {unknown} error - the rejection from the bulk-edit call
 * @returns {{key: string, detail?: string}} translation key plus any server detail
 */
export function resolveBulkActionError(error: unknown): {
  key: string;
  detail?: string;
} {
  const response = (error as {
    response?: { data?: { errors?: unknown; message?: string } };
  })?.response;

  const errors = response?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const first = errors[0] as { title?: string; detail?: string } | string;
    const detail =
      typeof first === 'string' ? first : first?.detail ?? first?.title;
    if (detail) return { key: 'stage.selection.confirm.error', detail };
  }

  if (response?.data?.message) {
    return { key: 'stage.selection.confirm.error', detail: response.data.message };
  }

  return { key: 'stage.selection.confirm.error' };
}

/**
 * The HTTP status behind a rejected request, when there is one.
 *
 * A narrow read of the axios error rather than a cast of the whole thing: the
 * only field callers need is the status, and claiming a shape for the rest
 * would be a lie the compiler cannot check. Returns `undefined` when the
 * request never got a response (network failure, timeout), which is exactly the
 * distinction the threshold modal needs — no response is not a 403.
 */
export function httpStatusOf(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;

  const response = (error as { response?: unknown }).response;
  if (typeof response !== 'object' || response === null) return undefined;

  const status = (response as { status?: unknown }).status;

  return typeof status === 'number' ? status : undefined;
}

/**
 * True when the rejection came from the transport layer rather than from a
 * `throw new Error(...)` in this module.
 *
 * Structural, like `httpStatusOf` above, instead of importing axios' own guard:
 * the only thing that matters here is "did a human write this message for a
 * customer, or did a library write it for a developer".
 */
function isTransportError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;

  if ((error as { isAxiosError?: unknown }).isAxiosError === true) return true;

  const response = (error as { response?: unknown }).response;

  return typeof response === 'object' && response !== null;
}

/**
 * The sentence to put in front of the customer when a funnel write fails.
 *
 * BUG-1041: every `updateFunnel` caller raised `(err as Error).message` with a
 * Spanish sentence behind a `||`. For an axios rejection that message is always
 * truthy — "Request failed with status code 403" — so the fallback never ran
 * and the raw string was what the customer actually saw, untranslated and
 * unactionable, while the real cause (a permission gate) went unsaid.
 *
 * Three cases, in order:
 *  - 403 → `forbidden`, the one status whose cause the customer can act on.
 *  - an `Error` thrown by hand in this module → its own message, which was
 *    already written to be read (e.g. "No se recibió información…").
 *  - anything else, including a network failure with no response → `fallback`,
 *    the caller's own sentence for its own operation.
 */
export function funnelWriteErrorMessage(
  error: unknown,
  forbidden: string,
  fallback: string,
): string {
  if (httpStatusOf(error) === 403) return forbidden;

  if (error instanceof Error && !isTransportError(error) && error.message) {
    return error.message;
  }

  return fallback;
}

// ── U8 · the board's red predicate (R11, R12 · KTD5) ─────────────────────────

/**
 * Parse a `last_action_at` stamp into epoch millis, or null when there is no
 * usable stamp.
 *
 * The null case is the whole reason this exists. Both BFFs marshal a Go
 * `time.Time`, so a lead leads-service never stamped does NOT arrive as null or
 * as an empty string: it arrives as `"0001-01-01T00:00:00Z"`, which is truthy.
 * `if (!stamp)` and `stamp ?? fallback` both wave it through, and the predicate
 * then measures two thousand years of inactivity and reddens every card on the
 * board at once. leads-service and the BFFs promote in separate gitops commits,
 * so there is a live window where exactly that value is on every card.
 *
 * Anything at or before the Unix epoch is therefore "no stamp": the year-1 zero
 * time lands far below it, and 1970 is not a real last-action date for a
 * WhatsApp CRM either.
 */
export function parseLastActionAt(
  lastActionAt?: string | null,
): number | null {
  if (!lastActionAt) return null;

  const parsed = moment(lastActionAt).valueOf();

  if (!Number.isFinite(parsed) || parsed <= 0) return null;

  return parsed;
}

/**
 * Mirrors `vw_leads`:
 *
 *   s.expires_after > 0
 *   AND date_trunc('minute', l.last_action_at + interval '1 minute' * s.expires_after) <= NOW()
 *
 * The deadline is truncated to the minute — so a stamp carrying seconds expires
 * up to 59 s early. That is deliberately copied rather than "fixed": a client
 * that disagreed with the view would flip a card red on the tick and back on
 * the next fetch.
 *
 * Monotonic in `now` for a fixed stamp and threshold, which is what makes the
 * tick one-way: it can only ever turn a card red (R12 — un-redding arrives
 * through a fetch or the socket, which move the stamp).
 */
export function isExpired(
  lastActionAt: string | null | undefined,
  expiresAfterMinutes: number | undefined,
  now: number,
): boolean {
  if (!expiresAfterMinutes || expiresAfterMinutes <= 0) return false;

  const stamp = parseLastActionAt(lastActionAt);

  if (stamp === null) return false;

  const deadline = stamp + expiresAfterMinutes * MINUTE_IN_MS;

  return Math.floor(deadline / MINUTE_IN_MS) * MINUTE_IN_MS <= now;
}

/**
 * Whole minutes of inactivity — the number the explainer's copy names (R14).
 */
export function elapsedMinutesSince(
  lastActionAt: string | null | undefined,
  now: number,
): number | null {
  const stamp = parseLastActionAt(lastActionAt);

  if (stamp === null) return null;

  return Math.floor((now - stamp) / MINUTE_IN_MS);
}

/**
 * One pass over the board: which cards are red right now, and which one turned
 * red first in board order (KTD7 — the Alert Center signal).
 *
 * A card is red when the SERVER said so at the last fetch, or when the tick's
 * predicate says so. The two are OR'd rather than the tick replacing the
 * fetched flag, so a stage whose threshold the client cannot evaluate (no
 * stamp on an older payload) still renders the server's verdict.
 */
export function deriveBoardExpiredState(input: {
  stages: tFunnelStage[] | null | undefined;
  now: number;
}): tBoardExpiredState {
  const { stages, now } = input;
  const expiredLeadIds = new Set<string>();
  let firstExpired: tBoardExpiredLead | null = null;

  (stages ?? []).forEach((stage) => {
    (stage.leads ?? []).forEach((lead) => {
      const red =
        lead.expired || isExpired(lead.lastActionAt, stage.expiresAfter, now);

      if (!red) return;

      expiredLeadIds.add(lead.id);

      if (firstExpired) return;

      firstExpired = {
        stageId: stage.id,
        stageName: stage.name,
        leadName: lead.name,
        // A server-expired card with no usable stamp still crossed its stage
        // threshold, so the threshold is the honest floor for "how long".
        elapsedMinutes:
          elapsedMinutesSince(lead.lastActionAt, now) ?? stage.expiresAfter,
      };
    });
  });

  return { expiredLeadIds, firstExpired };
}

// ---- AGI-412: deep link into the stagnant-leads threshold modal --------------

/** Query param the Home news card's "Leads estancados" CTA lands with
 * (`/workspace/funnel?open=stuck-leads`). Consumed once by the board and
 * stripped, mirroring the `?window=flows` contract of the WAPI page. */
export const STUCK_LEADS_DEEP_LINK_PARAM = 'open';
export const STUCK_LEADS_DEEP_LINK_VALUE = 'stuck-leads';

/**
 * Which stage the deep link opens the threshold modal for: the first regular
 * stage in board order (the inbox `NIL_UUID` and system stages carry no
 * counter, so the modal would only say "not available" there). `null` when
 * the visitor cannot edit stages — the modal's save is refused server-side for
 * sellers/viewers (RET-2018 U10), so opening it would be a door to a 403 — or
 * when the board has no regular stage yet.
 */
export function resolveStuckLeadsDeepLinkStage(input: {
  stages: tFunnelStage[] | null | undefined;
  canEditStages: boolean;
}): tFunnelStage | null {
  const { stages, canEditStages } = input;
  if (!canEditStages) return null;
  return (stages ?? []).find((stage) => stage.id !== NIL_UUID) ?? null;
}


/**
 * INT-1596: whether the "No entregados" chip (and its mirror in the filters
 * panel) shows. It is its own notification — there while N > 0, gone at 0 —
 * except while it is on: a board the filter emptied must keep the control
 * that undoes it.
 */
export function isUndeliveredChipVisible(
  count: number | null,
  isActive: boolean,
): boolean {
  return isActive || (count !== null && count > 0);
}

/** Where a card was before LEA-50 archived it from the board, to put it back. */
export type tRemovedBoardLead = {
  stageId: string;
  index: number;
  lead: tFunnelLead;
};

/**
 * LEA-50 (U22): a result saved with "Archivar" takes the card off the board.
 * The archive goes through Ragnarok, which sends no lead frame (BUG-1126), so
 * the board learns it from `leadActiveChangedEvent`. Same count rule as the
 * frame path: only the stage that held the card goes down.
 */
export function removeLeadFromStages(
  stages: tFunnelStage[],
  leadId: string,
): { stages: tFunnelStage[]; removed: tRemovedBoardLead | null } {
  let removed: tRemovedBoardLead | null = null;
  const next = stages.map((stage) => {
    const index = stage.leads?.findIndex((lead) => lead.id === leadId) ?? -1;
    if (index < 0 || !stage.leads) return stage;
    removed = { stageId: stage.id, index, lead: stage.leads[index] };
    return {
      ...stage,
      leads: stage.leads.filter((lead) => lead.id !== leadId),
      leadCount: Math.max(0, (stage.leadCount ?? 0) - 1),
    };
  });
  return removed ? { stages: next, removed } : { stages, removed: null };
}

/** "Devolver al embudo": the card goes back to its place; a second call changes nothing. */
export function restoreLeadToStages(
  stages: tFunnelStage[],
  removed: tRemovedBoardLead,
): tFunnelStage[] {
  const onBoard = stages.some((stage) =>
    stage.leads?.some((lead) => lead.id === removed.lead.id),
  );
  if (onBoard) return stages;
  return stages.map((stage) => {
    if (stage.id !== removed.stageId) return stage;
    const leads = [...(stage.leads ?? [])];
    leads.splice(Math.min(removed.index, leads.length), 0, removed.lead);
    return { ...stage, leads, leadCount: (stage.leadCount ?? 0) + 1 };
  });
}
